// Message Templates (PRD §4.4, WhatChimp Parity WP-01).
// Live data via Cloud Function callables: listTemplates, submitTemplate, templateStatus,
// importTemplates, uploadTemplateMedia, rewriteTemplateBody.
// Features:
// - Create all WhatChimp template types: General, Mixed, Carousel Media, Carousel Product, OTP
// - FilterBar with multi-account, status, category, search
// - DataTable with sort, pagination, quality scores, and real-time review sync
// - Import templates directly from Meta Business Manager
// - Live phone preview and copy/duplicate workflow

import { useState, useMemo } from 'react';
import { useI18n } from '../lib/i18n';
import { ALL_ACCOUNTS, numberLabel, useAccounts } from '../lib/accounts';
import {
  useTemplates,
  templateStatus,
  importTemplates,
  type TemplateView,
} from '../lib/whatsappApi';
import type { TplTemplateType } from '../lib/templateTypes';
import { getTemplateLocale } from '../lib/templateLocales';

import PageHeader from '../components/ui/PageHeader';
import StatCard from '../components/ui/StatCard';
import Card from '../components/ui/Card';
import FilterBar from '../components/ui/FilterBar';
import DataTable, { type Column } from '../components/ui/DataTable';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Drawer from '../components/ui/Drawer';
import EmptyState from '../components/ui/EmptyState';
import Skeleton from '../components/ui/Skeleton';
import { useToast } from '../components/ui/Toast';
import TemplateDrawer from '../components/templates/TemplateDrawer';
import TemplatePreview from '../components/templates/TemplatePreview';

export default function Templates() {
  const { t } = useI18n();
  const toast = useToast();
  const { items: allItems, initialLoaded, error, refresh } = useTemplates();
  const accounts = useAccounts();

  // Filters state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [categoryFilter, setCategoryFilter] = useState<string>('ALL');

  // Drawer / Composer state
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerType, setComposerType] = useState<TplTemplateType>('general');
  const [templateToEdit, setTemplateToEdit] = useState<TemplateView | null>(null);
  const [createMenuOpen, setCreateMenuOpen] = useState(false);

  // Quick Preview state
  const [previewTemplate, setPreviewTemplate] = useState<TemplateView | null>(null);

  // Syncing & Import state
  const [syncingIds, setSyncingIds] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);
  const [importToast, setImportToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Multi-number WABA scoping
  const scopedAccount = accounts.selectedId !== ALL_ACCOUNTS ? accounts.selected : null;
  const wabaScopedItems = useMemo(() => {
    if (!scopedAccount?.businessAccountId) return allItems;
    return allItems.filter(
      (tpl) => !tpl.wabaId || tpl.wabaId === scopedAccount.businessAccountId,
    );
  }, [allItems, scopedAccount]);

  // Filtered Items for DataTable
  const filteredItems = useMemo(() => {
    return wabaScopedItems.filter((tpl) => {
      // Category filter
      if (categoryFilter !== 'ALL' && tpl.category !== categoryFilter) {
        return false;
      }

      // Status filter
      if (statusFilter !== 'ALL' && tpl.status !== statusFilter) {
        return false;
      }

      // Search query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesName = tpl.name.toLowerCase().includes(q);
        const matchesBody = tpl.body?.toLowerCase().includes(q);
        const matchesLang = tpl.language?.toLowerCase().includes(q);
        if (!matchesName && !matchesBody && !matchesLang) {
          return false;
        }
      }

      return true;
    });
  }, [wabaScopedItems, categoryFilter, statusFilter, searchQuery]);

  // Statistics calculation
  const stats = useMemo(() => {
    const total = wabaScopedItems.length;
    let approved = 0;
    let pending = 0;
    let rejected = 0;

    for (const item of wabaScopedItems) {
      if (item.status === 'APPROVED') approved++;
      else if (item.status === 'PENDING') pending++;
      else if (item.status === 'REJECTED') rejected++;
    }

    return { total, approved, pending, rejected };
  }, [wabaScopedItems]);

  // Sync single template status from Meta
  async function handleSync(tpl: TemplateView) {
    setSyncingIds((prev) => new Set(prev).add(tpl.id));
    try {
      const res = await templateStatus({
        name: tpl.name,
        language: tpl.language,
        integrationId: tpl.integrationId || accounts.sendFromId || undefined,
      });
      if (res.found) {
        toast.success(`${tpl.name} (${tpl.language}): ${res.status || 'Updated'}`);
      } else {
        toast.error(`${tpl.name} not found on Meta`);
      }
      await refresh();
    } catch (err) {
      toast.error((err as Error).message || 'Failed to refresh template status');
    } finally {
      setSyncingIds((prev) => {
        const next = new Set(prev);
        next.delete(tpl.id);
        return next;
      });
    }
  }

  // Import / Sync all templates from Meta
  async function handleImportFromMeta() {
    setImporting(true);
    setImportToast(null);
    try {
      const res = await importTemplates({
        integrationId: accounts.sendFromId || undefined,
      });
      const failuresCount = res.failures?.length ?? 0;
      const failureText = failuresCount > 0 ? ` (${failuresCount} failed)` : '';
      setImportToast({
        message: (t('tpl.import.success') || 'Successfully imported/updated {count} templates from Meta ({total} found).')
          .replace('{count}', String(res.imported))
          .replace('{total}', String(res.total)) + failureText,
        type: failuresCount > 0 && res.imported === 0 ? 'error' : 'success',
      });
      await refresh();
    } catch (err) {
      setImportToast({
        message: (err as Error).message || 'Failed to import templates from Meta.',
        type: 'error',
      });
    } finally {
      setImporting(false);
      setTimeout(() => setImportToast(null), 6000);
    }
  }

  function openComposer(type: TplTemplateType, copyTemplate?: TemplateView) {
    setComposerType(type);
    setTemplateToEdit(copyTemplate || null);
    setComposerOpen(true);
    setCreateMenuOpen(false);
  }

  // Derive template type for badge
  function getTemplateTypeDisplay(tpl: TemplateView): string {
    if (!tpl.components || tpl.components.length === 0) {
      return tpl.headerMedia ? 'Media' : 'Text';
    }
    const hasCarousel = tpl.components.some((c) => c.type === 'CAROUSEL');
    if (hasCarousel) return 'Carousel';
    const hasMedia = tpl.components.some(
      (c) => c.type === 'HEADER' && c.format && c.format !== 'TEXT',
    );
    if (hasMedia || tpl.headerMedia) return 'Media';
    const hasButtons = tpl.components.some((c) => c.type === 'BUTTONS');
    if (hasButtons) return 'Interactive';
    return 'Standard';
  }

  // DataTable columns definition
  const columns: Column<TemplateView>[] = [
    {
      key: 'name',
      header: t('tpl.col.name') || 'Template Name',
      sortable: true,
      render: (row) => (
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1.5">
            <span className="font-mono text-xs font-bold text-slate-900">{row.name}</span>
            <button
              type="button"
              onClick={() => navigator.clipboard.writeText(row.name)}
              title="Copy name"
              className="text-slate-400 hover:text-slate-700 text-[11px]"
            >
              📋
            </button>
          </div>
          {row.body && (
            <p className="line-clamp-1 text-[11px] text-slate-500 max-w-[280px]">
              {row.body}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'category',
      header: t('tpl.col.category') || 'Category',
      sortable: true,
      render: (row) => {
        const variant =
          row.category === 'MARKETING'
            ? 'brand'
            : row.category === 'UTILITY'
            ? 'info'
            : 'slate';
        return <Badge variant={variant} size="sm">{row.category}</Badge>;
      },
    },
    {
      key: 'type',
      header: t('tpl.col.type') || 'Type',
      render: (row) => (
        <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700">
          {getTemplateTypeDisplay(row)}
        </span>
      ),
    },
    {
      key: 'language',
      header: t('tpl.col.language') || 'Language',
      sortable: true,
      render: (row) => {
        const loc = getTemplateLocale(row.language);
        return (
          <span className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
            <span className="font-mono font-bold uppercase text-[11px]">{row.language}</span>
            {loc && <span className="text-[11px] text-slate-400">· {loc.name}</span>}
          </span>
        );
      },
    },
    {
      key: 'waba',
      header: t('tpl.col.account') || 'Number / WABA',
      render: (row) => {
        const acc = accounts.numbers.find(
          (n) =>
            (row.integrationId && n.id === row.integrationId) ||
            (row.wabaId && n.businessAccountId === row.wabaId),
        );
        return (
          <span className="inline-flex items-center gap-1 font-mono text-[11px] text-slate-600 dark:text-slate-300">
            <span>📱</span>
            <span>
              {acc
                ? numberLabel(acc)
                : row.wabaId
                ? `#${row.wabaId.slice(-6)}`
                : t('templates.allAccounts') || 'Default'}
            </span>
          </span>
        );
      },
    },
    {
      key: 'status',
      header: t('tpl.col.status') || 'Status',
      sortable: true,
      render: (row) => {
        const variant =
          row.status === 'APPROVED'
            ? 'success'
            : row.status === 'PENDING'
            ? 'warning'
            : row.status === 'REJECTED'
            ? 'danger'
            : 'neutral';
        return (
          <Badge variant={variant} size="sm" dot>
            {row.status}
          </Badge>
        );
      },
    },
    {
      key: 'quality',
      header: t('tpl.col.quality') || 'Quality',
      render: (row) => {
        if (!row.qualityScore) {
          return <span className="text-slate-400 text-xs">—</span>;
        }
        const isGreen = row.qualityScore === 'GREEN' || row.qualityScore === 'HIGH';
        const isYellow = row.qualityScore === 'YELLOW' || row.qualityScore === 'MEDIUM';
        return (
          <span
            className={`text-xs font-semibold ${
              isGreen ? 'text-emerald-600' : isYellow ? 'text-amber-600' : 'text-red-600'
            }`}
          >
            {row.qualityScore}
          </span>
        );
      },
    },
    {
      key: 'submittedAt',
      header: t('tpl.col.updated') || 'Updated',
      sortable: true,
      render: (row) => {
        const ts = row.updatedAt || row.submittedAt;
        return (
          <span className="text-xs text-slate-500">
            {ts ? new Date(ts).toLocaleDateString() : '—'}
          </span>
        );
      },
    },
    {
      key: 'actions',
      header: t('tpl.col.actions') || 'Actions',
      align: 'end',
      render: (row) => {
        const isSyncing = syncingIds.has(row.id);
        return (
          <div className="flex items-center justify-end gap-1.5">
            {/* Sync Button */}
            <button
              type="button"
              onClick={() => handleSync(row)}
              disabled={isSyncing}
              title={t('tpl.sync') || 'Sync review status with Meta'}
              className="rounded-lg border border-slate-200 bg-white p-1.5 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >
              {isSyncing ? (
                <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
              ) : (
                <span>🔄</span>
              )}
            </button>

            {/* Quick Preview Button */}
            <button
              type="button"
              onClick={() => setPreviewTemplate(row)}
              title={t('tpl.preview') || 'Preview'}
              className="rounded-lg border border-slate-200 bg-white p-1.5 text-xs text-slate-600 hover:bg-slate-50"
            >
              👁️
            </button>

            {/* Duplicate / Edit Copy */}
            <button
              type="button"
              onClick={() => openComposer('general', row)}
              title={t('tpl.duplicate') || 'Duplicate template'}
              className="rounded-lg border border-slate-200 bg-white p-1.5 text-xs text-slate-600 hover:bg-slate-50"
            >
              📋
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header with Action Buttons */}
      <PageHeader
        title={t('tpl.title') || 'Message Templates'}
        subtitle={
          <span>
            {t('tpl.subtitle') || 'Design, preview, and submit Meta WhatsApp Cloud API templates.'}
            {accounts.numbers.length > 1 && (
              <> · {t('templates.forAccount') || 'For Account:'} <b className="text-slate-900">{scopedAccount ? numberLabel(scopedAccount) : t('templates.allAccounts') || 'All Numbers'}</b></>
            )}
          </span>
        }
        action={
          <div className="flex items-center gap-2 relative">
            {/* Sync with Meta Button */}
            <Button
              variant="secondary"
              onClick={handleImportFromMeta}
              disabled={importing}
              loading={importing}
            >
              <span>🔄</span>
              <span>{t('tpl.syncMeta') || 'Sync with Meta'}</span>
            </Button>

            {/* Create Template Dropdown Trigger */}
            <div className="relative">
              <Button
                variant="primary"
                onClick={() => setCreateMenuOpen(!createMenuOpen)}
              >
                <span>+</span>
                <span>{t('tpl.create') || 'Create template'}</span>
                <span className="text-[10px]">▾</span>
              </Button>

              {createMenuOpen && (
                <div className="absolute end-0 top-full z-30 mt-1.5 w-60 rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl divide-y divide-slate-100 animate-in fade-in zoom-in-95 duration-150">
                  <div className="pb-1">
                    <button
                      type="button"
                      onClick={() => openComposer('general')}
                      className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-start text-xs font-semibold text-slate-800 hover:bg-indigo-50 hover:text-indigo-700"
                    >
                      <span className="text-base">📝</span>
                      <div>
                        <div>General Template</div>
                        <div className="text-[10px] text-slate-400 font-normal">Header, body, footer, buttons</div>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => openComposer('mixed')}
                      className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-start text-xs font-semibold text-slate-800 hover:bg-indigo-50 hover:text-indigo-700"
                    >
                      <span className="text-base">🔀</span>
                      <div>
                        <div>Mixed Buttons Template</div>
                        <div className="text-[10px] text-slate-400 font-normal">URL, Phone, Quick Reply & Code</div>
                      </div>
                    </button>
                  </div>

                  <div className="py-1">
                    <button
                      type="button"
                      onClick={() => openComposer('carousel_media')}
                      className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-start text-xs font-semibold text-slate-800 hover:bg-indigo-50 hover:text-indigo-700"
                    >
                      <span className="text-base">🎠</span>
                      <div>
                        <div>Carousel (Media)</div>
                        <div className="text-[10px] text-slate-400 font-normal">2 to 10 scrollable image/video cards</div>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => openComposer('carousel_product')}
                      className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-start text-xs font-semibold text-slate-800 hover:bg-indigo-50 hover:text-indigo-700"
                    >
                      <span className="text-base">🛍️</span>
                      <div>
                        <div>Carousel (Product)</div>
                        <div className="text-[10px] text-slate-400 font-normal">Cards synced with catalog products</div>
                      </div>
                    </button>
                  </div>

                  <div className="pt-1">
                    <button
                      type="button"
                      onClick={() => openComposer('otp')}
                      className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-start text-xs font-semibold text-slate-800 hover:bg-indigo-50 hover:text-indigo-700"
                    >
                      <span className="text-base">🔑</span>
                      <div>
                        <div>Authentication (OTP)</div>
                        <div className="text-[10px] text-slate-400 font-normal">One-time passcode verification</div>
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        }
      />

      {/* Import Notification Toast */}
      {importToast && (
        <div
          className={`flex items-center justify-between rounded-xl p-3.5 text-xs font-semibold shadow-sm animate-in fade-in ${
            importToast.type === 'success'
              ? 'border border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border border-red-200 bg-red-50 text-red-800'
          }`}
        >
          <span>{importToast.message}</span>
          <button
            type="button"
            onClick={() => setImportToast(null)}
            className="ms-3 text-slate-400 hover:text-slate-700"
          >
            ✕
          </button>
        </div>
      )}

      {/* Stat Cards Row */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {!initialLoaded ? (
          <>
            <Skeleton.StatCard />
            <Skeleton.StatCard />
            <Skeleton.StatCard />
            <Skeleton.StatCard />
          </>
        ) : (
          <>
            <StatCard
              label={t('tpl.stats.total') || 'Total Templates'}
              value={stats.total}
              variant="indigo"
              icon={<span>🧩</span>}
            />
            <StatCard
              label={t('tpl.stats.approved') || 'Approved'}
              value={stats.approved}
              variant="green"
              icon={<span>✅</span>}
            />
            <StatCard
              label={t('tpl.stats.pending') || 'In Review (Pending)'}
              value={stats.pending}
              variant="amber"
              icon={<span>⏳</span>}
            />
            <StatCard
              label={t('tpl.stats.rejected') || 'Rejected / Disabled'}
              value={stats.rejected}
              variant="rose"
              icon={<span>⚠️</span>}
            />
          </>
        )}
      </div>

      {/* Main Content Table Card */}
      <Card noPadding>
        {/* Filter Bar */}
        <FilterBar
          search={{
            value: searchQuery,
            onChange: setSearchQuery,
            placeholder: t('tpl.filter.searchPlaceholder') || 'Search templates by name or text…',
          }}
        >
          {/* Category Filter */}
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 outline-none focus:border-indigo-500"
          >
            <option value="ALL">{t('tpl.filter.allCategories') || 'All Categories'}</option>
            <option value="MARKETING">Marketing</option>
            <option value="UTILITY">Utility</option>
            <option value="AUTHENTICATION">Authentication</option>
          </select>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 outline-none focus:border-indigo-500"
          >
            <option value="ALL">{t('tpl.filter.allStatuses') || 'All Statuses'}</option>
            <option value="APPROVED">Approved</option>
            <option value="PENDING">In Review</option>
            <option value="REJECTED">Rejected</option>
          </select>
        </FilterBar>

        {/* Error message */}
        {error && (
          <div className="m-4 rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-700">
            {error}
          </div>
        )}

        {/* Templates DataTable */}
        <DataTable
          data={filteredItems}
          columns={columns}
          loading={!initialLoaded}
          emptyState={
            <EmptyState
              icon={<div className="text-3xl">🧩</div>}
              title={
                searchQuery || statusFilter !== 'ALL' || categoryFilter !== 'ALL'
                  ? (t('tpl.empty.filteredTitle') || 'No matching templates')
                  : (t('tpl.empty.title') || 'No templates yet')
              }
              description={
                searchQuery || statusFilter !== 'ALL' || categoryFilter !== 'ALL'
                  ? (t('tpl.empty.filteredDesc') || 'Try adjusting your search query or category/status filters.')
                  : (t('tpl.empty.desc') || 'Create your first WhatsApp template or sync existing ones from Meta Business Manager.')
              }
              action={
                <div className="flex flex-wrap items-center justify-center gap-2.5">
                  <Button variant="primary" onClick={() => setCreateMenuOpen(true)}>
                    <span>+</span>
                    <span>{t('tpl.create') || 'Create template'}</span>
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleImportFromMeta}
                    disabled={importing}
                    loading={importing}
                  >
                    <span>🔄</span>
                    <span>{t('tpl.syncMeta') || 'Sync with Meta'}</span>
                  </Button>
                </div>
              }
            />
          }
        />
      </Card>

      {/* Main Template Creation / Edit Drawer */}
      <TemplateDrawer
        open={composerOpen}
        onClose={() => {
          setComposerOpen(false);
          setTemplateToEdit(null);
        }}
        onSubmitted={() => {
          void refresh();
        }}
        initialType={composerType}
        templateToEdit={templateToEdit}
        integrationId={accounts.sendFromId}
      />

      {/* Read-Only Quick Preview Drawer */}
      {previewTemplate && (
        <Drawer
          open={!!previewTemplate}
          onClose={() => setPreviewTemplate(null)}
          title={
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm">{previewTemplate.name}</span>
              <Badge variant={previewTemplate.status === 'APPROVED' ? 'success' : 'warning'} size="sm">
                {previewTemplate.status}
              </Badge>
            </div>
          }
          subtitle={`Language: ${previewTemplate.language} · Category: ${previewTemplate.category}`}
          size="md"
        >
          <div className="py-4">
            <TemplatePreview
              headerType={
                previewTemplate.components?.find((c) => c.type === 'HEADER')?.format ||
                (previewTemplate.headerMedia ? 'IMAGE' : 'NONE')
              }
              headerText={previewTemplate.components?.find((c) => c.type === 'HEADER')?.text}
              body={previewTemplate.body}
              footer={previewTemplate.components?.find((c) => c.type === 'FOOTER')?.text}
              buttons={
                previewTemplate.components?.find((c) => c.type === 'BUTTONS')?.buttons as any
              }
              variables={previewTemplate.variables}
            />
          </div>
        </Drawer>
      )}
    </div>
  );
}
